import React, { useState, useEffect } from 'react';
import { FormData, Stand } from '../types/stand.types';
import { tiposOrganizacion, areas, subareasPorArea } from '../data/standsData';
import PhoneInput from './PhoneInput';
// import OrganizationCombobox from './OrganizationCombobox'; // Temporalmente desactivado
import MultiSelectTags from './MultiSelectTags';
import SearchableSelect from './SearchableSelect';
import DuplicateEmailModal from './DuplicateEmailModal';

// Lista de países para el componente PhoneInput
const countries = [
  { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
  { code: 'BR', name: 'Brasil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱' },
  { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
  { code: 'MX', name: 'México', dialCode: '+52', flag: '🇲🇽' },
  { code: 'PE', name: 'Perú', dialCode: '+51', flag: '🇵🇪' },
  { code: 'UY', name: 'Uruguay', dialCode: '+598', flag: '🇺🇾' },
  { code: 'VE', name: 'Venezuela', dialCode: '+58', flag: '🇻🇪' },
  { code: 'US', name: 'Estados Unidos', dialCode: '+1', flag: '🇺🇸' },
  { code: 'ES', name: 'España', dialCode: '+34', flag: '🇪🇸' },
  { code: 'IT', name: 'Italia', dialCode: '+39', flag: '🇮🇹' },
  { code: 'FR', name: 'Francia', dialCode: '+33', flag: '🇫🇷' },
  { code: 'DE', name: 'Alemania', dialCode: '+49', flag: '🇩🇪' },
  { code: 'GB', name: 'Reino Unido', dialCode: '+44', flag: '🇬🇧' },
  { code: 'CA', name: 'Canadá', dialCode: '+1', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'JP', name: 'Japón', dialCode: '+81', flag: '🇯🇵' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'RU', name: 'Rusia', dialCode: '+7', flag: '🇷🇺' }
];


interface ReservationFormProps {
  onSubmit: (data: FormData, action?: 'replace' | 'add' | 'new', previousQueryId?: string) => void;
  selectedStandsCount: number;
  onReset?: () => void;
  showSuccessMessage?: boolean;
  selectedStands: Stand[];
  onCheckDuplicateEmail?: (email: string) => Promise<{
    isDuplicate: boolean;
    previousData?: {
      stands: Stand[];
      organization: string;
      queryId?: string;
    };
  }>;
}

const ReservationForm: React.FC<ReservationFormProps> = ({ 
  onSubmit, 
  selectedStandsCount, 
  onReset, 
  showSuccessMessage, 
  selectedStands,
  onCheckDuplicateEmail 
}) => {
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    codigoPais: 'AR',
    numeroTelefono: '',
    cargo: '',
    area: '',
    subarea: '',
    organizacion: '',
    sitioWeb: '',
    tipoOrganizacion: '',
    industrias: [],
    mensaje: '',
    autorizoWhatsApp: false,
    deseoNovedades: false
  });

  const [emailError, setEmailError] = useState<string>('');
  const [sitioWebError, setSitioWebError] = useState<string>('');
  
  // Estados para el modal de email duplicado
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateData, setDuplicateData] = useState<{
    previousStands: Stand[];
    previousOrganization: string;
    queryId?: string;
  } | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUrl = (url: string): boolean => {
    if (url.trim() === '') return false; // Campo obligatorio
    
    // Si ya tiene protocolo, validar URL completa
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
      return urlRegex.test(url);
    }
    
    // Si no tiene protocolo, validar solo dominio
    const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return domainRegex.test(url);
  };

  // Obtener subáreas dinámicas basadas en el área seleccionada
  const getSubareasDinamicas = () => {
    if (!formData.area) {
      return [];
    }
    return subareasPorArea[formData.area as keyof typeof subareasPorArea]?.subareas || [];
  };

  const subareasDinamicas = getSubareasDinamicas();

  // Función para capitalizar nombres (primera letra de cada palabra en mayúscula)
  const capitalizeName = (text: string): string => {
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Aplicar capitalización automática para nombre y apellido
    if (name === 'nombre' || name === 'apellido') {
      setFormData(prev => ({
        ...prev,
        [name]: capitalizeName(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Validar email en tiempo real
    if (name === 'email') {
      if (value.trim() === '') {
        setEmailError('');
      } else if (!validateEmail(value)) {
        setEmailError('Por favor, ingresa una dirección de correo válida');
      } else {
        setEmailError('');
      }
    }

    // Validar sitio web en tiempo real
    if (name === 'sitioWeb') {
      if (value.trim() === '') {
        setSitioWebError('');
      } else if (!validateUrl(value)) {
        setSitioWebError('Por favor, ingresa una URL válida');
      } else {
        setSitioWebError('');
      }
    }
  };

  const handleSitioWebBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    
    // Si el usuario escribió algo y no tiene protocolo, agregar https://
    if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
      // Verificar si parece un dominio válido
      const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
      if (domainRegex.test(value)) {
        setFormData(prev => ({
          ...prev,
          sitioWeb: `https://${value}`
        }));
      }
    }
  };

  // Función para manejar cambios en el área
  const handleAreaChange = (value: string) => {
    setFormData(prev => {
      // Limpiar subárea si el área cambió
      const areaCambio = prev.area !== value;
      
      return {
        ...prev,
        area: value,
        subarea: areaCambio ? '' : prev.subarea
      };
    });
  };

  const handleCodigoPaisChange = (codigo: string) => {
    setFormData(prev => ({
      ...prev,
      codigoPais: codigo,
      telefono: `${countries.find(c => c.code === codigo)?.dialCode || ''} ${prev.numeroTelefono}`.trim()
    }));
  };

  const handleNumeroTelefonoChange = (numero: string) => {
    const selectedCountry = countries.find(c => c.code === formData.codigoPais);
    setFormData(prev => ({
      ...prev,
      numeroTelefono: numero,
      telefono: `${selectedCountry?.dialCode || ''} ${numero}`.trim()
    }));
  };

  // Función de validación de teléfono (misma lógica que PhoneInput)
  const validatePhoneNumber = (countryCode: string, phoneNumber: string): boolean => {
    if (!phoneNumber.trim()) return false; // Required field
    
    // Remove all non-digit characters
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // Comprehensive validation patterns for all countries
    const patterns: { [key: string]: RegExp } = {
      // América Latina
      'AR': /^\d{10,11}$/, // Argentina: 10-11 digits
      'BR': /^\d{10,11}$/, // Brasil: 10-11 digits
      'CL': /^\d{8,9}$/,   // Chile: 8-9 digits
      'CO': /^\d{10}$/,    // Colombia: 10 digits
      'MX': /^\d{10}$/,    // México: 10 digits
      'PE': /^\d{9}$/,     // Perú: 9 digits
      'UY': /^\d{8,9}$/,   // Uruguay: 8-9 digits
      'VE': /^\d{10,11}$/, // Venezuela: 10-11 digits
      'BO': /^\d{8}$/,     // Bolivia: 8 digits
      'EC': /^\d{9}$/,     // Ecuador: 9 digits
      'PY': /^\d{9}$/,     // Paraguay: 9 digits
      'CR': /^\d{8}$/,     // Costa Rica: 8 digits
      'PA': /^\d{8}$/,     // Panamá: 8 digits
      'GT': /^\d{8}$/,     // Guatemala: 8 digits
      'HN': /^\d{8}$/,     // Honduras: 8 digits
      'SV': /^\d{8}$/,     // El Salvador: 8 digits
      'NI': /^\d{8}$/,     // Nicaragua: 8 digits
      'CU': /^\d{8}$/,     // Cuba: 8 digits
      'DO': /^\d{10}$/,    // República Dominicana: 10 digits
      'HT': /^\d{8}$/,     // Haití: 8 digits
      'JM': /^\d{10}$/,    // Jamaica: 10 digits
      'TT': /^\d{10}$/,    // Trinidad y Tobago: 10 digits

      // América del Norte
      'US': /^\d{10}$/,    // Estados Unidos: 10 digits
      'CA': /^\d{10}$/,    // Canadá: 10 digits

      // Europa
      'ES': /^\d{9}$/,     // España: 9 digits
      'IT': /^\d{10,11}$/, // Italia: 10-11 digits
      'FR': /^\d{10}$/,    // Francia: 10 digits
      'DE': /^\d{10,11}$/, // Alemania: 10-11 digits
      'GB': /^\d{10,11}$/, // Reino Unido: 10-11 digits
      'PT': /^\d{9}$/,     // Portugal: 9 digits
      'NL': /^\d{9}$/,     // Países Bajos: 9 digits
      'BE': /^\d{9}$/,     // Bélgica: 9 digits
      'CH': /^\d{9}$/,     // Suiza: 9 digits
      'AT': /^\d{10,11}$/, // Austria: 10-11 digits
      'SE': /^\d{9}$/,     // Suecia: 9 digits
      'NO': /^\d{8}$/,     // Noruega: 8 digits
      'DK': /^\d{8}$/,     // Dinamarca: 8 digits
      'FI': /^\d{9,10}$/,  // Finlandia: 9-10 digits
      'PL': /^\d{9}$/,     // Polonia: 9 digits
      'CZ': /^\d{9}$/,     // República Checa: 9 digits
      'HU': /^\d{9}$/,     // Hungría: 9 digits
      'RO': /^\d{10}$/,    // Rumania: 10 digits
      'BG': /^\d{9}$/,     // Bulgaria: 9 digits
      'GR': /^\d{10}$/,    // Grecia: 10 digits
      'RU': /^\d{10,11}$/, // Rusia: 10-11 digits
      'UA': /^\d{9}$/,     // Ucrania: 9 digits
      'IE': /^\d{9}$/,     // Irlanda: 9 digits
      'IS': /^\d{7}$/,     // Islandia: 7 digits

      // Asia
      'CN': /^\d{11}$/,    // China: 11 digits
      'JP': /^\d{10,11}$/, // Japón: 10-11 digits
      'KR': /^\d{10,11}$/, // Corea del Sur: 10-11 digits
      'IN': /^\d{10}$/,    // India: 10 digits
      'TH': /^\d{9}$/,     // Tailandia: 9 digits
      'SG': /^\d{8}$/,     // Singapur: 8 digits
      'MY': /^\d{9,10}$/,  // Malasia: 9-10 digits
      'ID': /^\d{10,12}$/, // Indonesia: 10-12 digits
      'PH': /^\d{10}$/,    // Filipinas: 10 digits
      'VN': /^\d{9,10}$/,  // Vietnam: 9-10 digits
      'TW': /^\d{9}$/,     // Taiwán: 9 digits
      'HK': /^\d{8}$/,     // Hong Kong: 8 digits
      'BD': /^\d{10}$/,    // Bangladesh: 10 digits
      'PK': /^\d{10}$/,    // Pakistán: 10 digits
      'LK': /^\d{9}$/,     // Sri Lanka: 9 digits
      'NP': /^\d{10}$/,    // Nepal: 10 digits
      'MM': /^\d{9,10}$/,  // Myanmar: 9-10 digits
      'KH': /^\d{8,9}$/,   // Camboya: 8-9 digits
      'LA': /^\d{8,9}$/,   // Laos: 8-9 digits
      'MN': /^\d{8}$/,     // Mongolia: 8 digits

      // Oceanía
      'AU': /^\d{9,10}$/,  // Australia: 9-10 digits
      'NZ': /^\d{8,9}$/,   // Nueva Zelanda: 8-9 digits
      'FJ': /^\d{7,8}$/,   // Fiyi: 7-8 digits
      'PG': /^\d{8}$/,     // Papúa Nueva Guinea: 8 digits
      'NC': /^\d{6}$/,     // Nueva Caledonia: 6 digits

      // África
      'ZA': /^\d{9}$/,     // Sudáfrica: 9 digits
      'EG': /^\d{10}$/,    // Egipto: 10 digits
      'NG': /^\d{10,11}$/, // Nigeria: 10-11 digits
      'KE': /^\d{9,10}$/,  // Kenia: 9-10 digits
      'MA': /^\d{9}$/,     // Marruecos: 9 digits
      'TN': /^\d{8}$/,     // Túnez: 8 digits
      'DZ': /^\d{9}$/,     // Argelia: 9 digits
      'GH': /^\d{9}$/,     // Ghana: 9 digits
      'ET': /^\d{9}$/,     // Etiopía: 9 digits
      'UG': /^\d{9}$/,     // Uganda: 9 digits
      'TZ': /^\d{9}$/,     // Tanzania: 9 digits
      'ZW': /^\d{9}$/,     // Zimbabue: 9 digits
      'BW': /^\d{7,8}$/,   // Botsuana: 7-8 digits
      'NA': /^\d{9}$/,     // Namibia: 9 digits
      'ZM': /^\d{9}$/,     // Zambia: 9 digits
      'MW': /^\d{9}$/,     // Malaui: 9 digits
      'MZ': /^\d{9}$/,     // Mozambique: 9 digits
      'MG': /^\d{9}$/,     // Madagascar: 9 digits
      'MU': /^\d{7,8}$/,   // Mauricio: 7-8 digits
      'SC': /^\d{7}$/,     // Seychelles: 7 digits

      // Oriente Medio
      'SA': /^\d{9}$/,     // Arabia Saudí: 9 digits
      'AE': /^\d{9}$/,     // Emiratos Árabes Unidos: 9 digits
      'QA': /^\d{8}$/,     // Catar: 8 digits
      'KW': /^\d{8}$/,     // Kuwait: 8 digits
      'BH': /^\d{8}$/,     // Baréin: 8 digits
      'OM': /^\d{8}$/,     // Omán: 8 digits
      'JO': /^\d{9}$/,     // Jordania: 9 digits
      'LB': /^\d{8}$/,     // Líbano: 8 digits
      'SY': /^\d{9}$/,     // Siria: 9 digits
      'IQ': /^\d{10}$/,    // Irak: 10 digits
      'IR': /^\d{10}$/,    // Irán: 10 digits
      'IL': /^\d{9}$/,     // Israel: 9 digits
      'TR': /^\d{10}$/     // Turquía: 10 digits
    };

    const pattern = patterns[countryCode];
    return pattern ? pattern.test(cleanNumber) : cleanNumber.length >= 7 && cleanNumber.length <= 15;
  };

  const isFormValid = () => {
    return formData.nombre.trim() !== '' &&
           formData.apellido.trim() !== '' &&
           formData.email.trim() !== '' &&
           validateEmail(formData.email) &&
           formData.numeroTelefono.trim() !== '' &&
           validatePhoneNumber(formData.codigoPais, formData.numeroTelefono) &&
           formData.cargo !== '' &&
           formData.area !== '' &&
           (subareasDinamicas.length === 0 || formData.subarea !== '') &&
           formData.organizacion.trim() !== '' &&
           formData.sitioWeb.trim() !== '' &&
           validateUrl(formData.sitioWeb) &&
           formData.tipoOrganizacion !== '' &&
           formData.industrias.length > 0 &&
           selectedStandsCount > 0;
  };

  const getButtonText = () => {
    if (isFormValid()) {
      return 'ENVIAR CONSULTA';
    }
    if (selectedStandsCount === 0) {
      return 'Selecciona al menos un stand';
    }
    return 'Completa los campos obligatorios';
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      codigoPais: 'AR',
      numeroTelefono: '',
      cargo: '',
      area: '',
      subarea: '',
      organizacion: '',
      sitioWeb: '',
      tipoOrganizacion: '',
      industrias: [],
      mensaje: '',
      autorizoWhatsApp: false,
      deseoNovedades: false
    });
    setEmailError('');
    setSitioWebError('');
  };

  // Función para verificar email duplicado
  const checkDuplicateEmail = async (email: string) => {
    if (!onCheckDuplicateEmail) return { isDuplicate: false };
    
    try {
      const result = await onCheckDuplicateEmail(email);
      return result;
    } catch (error) {
      console.error('Error checking duplicate email:', error);
      return { isDuplicate: false };
    }
  };

  // Función para manejar el envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) return;

    // Verificar si el email ya existe
    if (onCheckDuplicateEmail) {
      const duplicateResult = await checkDuplicateEmail(formData.email);
      
      if (duplicateResult.isDuplicate && duplicateResult.previousData) {
        // Mostrar modal de email duplicado
        setDuplicateData({
          previousStands: duplicateResult.previousData.stands,
          previousOrganization: duplicateResult.previousData.organization,
          queryId: duplicateResult.previousData.queryId
        });
        setShowDuplicateModal(true);
        return;
      }
    }

    // Si no hay duplicado, enviar directamente
    submitForm();
  };

  // Función para enviar el formulario
  const submitForm = (action: 'replace' | 'add' | 'new' = 'new', previousQueryId?: string) => {
    onSubmit(formData, action, previousQueryId);
    resetForm();
    if (onReset) {
      onReset();
    }
  };

  // Funciones para manejar las opciones del modal
  const handleReplaceQuery = () => {
    setShowDuplicateModal(false);
    submitForm('replace', duplicateData?.queryId);
  };

  const handleAddToQuery = () => {
    setShowDuplicateModal(false);
    submitForm('add', duplicateData?.queryId);
  };

  const handleNewQuery = () => {
    setShowDuplicateModal(false);
    submitForm('new');
  };

  const handleCloseModal = () => {
    setShowDuplicateModal(false);
    setDuplicateData(null);
  };

  return (
    <div className="form-container">
      <h2 className="form-title">Consulta de Stands</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="nombre" className="form-label">Nombre</label>
            <input
              type="text"
              id="nombre"
              name="nombre"
              className="form-input"
              value={formData.nombre}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="apellido" className="form-label">Apellido</label>
            <input
              type="text"
              id="apellido"
              name="apellido"
              className="form-input"
              value={formData.apellido}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email" className="form-label">Correo Electrónico</label>
            <input
              type="email"
              id="email"
              name="email"
              className={`form-input ${emailError ? 'error' : ''}`}
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            {emailError && (
              <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{emailError}</div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="telefono" className="form-label">Teléfono</label>
            <PhoneInput
              codigoPais={formData.codigoPais}
              numeroTelefono={formData.numeroTelefono}
              onCodigoPaisChange={handleCodigoPaisChange}
              onNumeroTelefonoChange={handleNumeroTelefonoChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="organizacion" className="form-label">Organización</label>
            <input
              type="text"
              id="organizacion"
              name="organizacion"
              className="form-input"
              value={formData.organizacion}
              onChange={handleInputChange}
              required
              placeholder="Ingrese el nombre de la organización"
            />
            {/* OrganizationCombobox temporalmente desactivado - se puede reactivar más adelante */}
            {/* <OrganizationCombobox
              value={formData.organizacion}
              onChange={(value) => setFormData(prev => ({ ...prev, organizacion: value }))}
            /> */}
          </div>
          <div className="form-group">
            <label htmlFor="tipoOrganizacion" className="form-label">Tipo de Organización</label>
            <SearchableSelect
              value={formData.tipoOrganizacion}
              onChange={(value) => setFormData(prev => ({ ...prev, tipoOrganizacion: value }))}
              options={tiposOrganizacion}
              placeholder="Seleccionar tipo de organización..."
              allowCustomInput={false}
            />
          </div>
        </div>

        <div className="form-row">
            <div className="form-group">
              <label htmlFor="cargo" className="form-label">Cargo</label>
              <input
                type="text"
                id="cargo"
                name="cargo"
                className="form-input"
                value={formData.cargo}
                onChange={handleInputChange}
                required
                placeholder="Ingrese su cargo"
              />
            </div>
          <div className="form-group">
            <label htmlFor="area" className="form-label">Área</label>
            <SearchableSelect
              value={formData.area}
              onChange={handleAreaChange}
              options={areas}
              placeholder="Seleccionar área..."
              allowCustomInput={false}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="subarea" className="form-label">Subárea</label>
            <SearchableSelect
              value={formData.subarea}
              onChange={(value) => setFormData(prev => ({ ...prev, subarea: value }))}
              options={subareasDinamicas}
              placeholder={formData.area ? "Seleccionar subárea..." : "Primero seleccione el área"}
              allowCustomInput={false}
              disabled={!formData.area || subareasDinamicas.length === 0}
            />
          </div>
          <div className="form-group">
            <label htmlFor="sitioWeb" className="form-label">Sitio Web</label>
            <input
              type="url"
              id="sitioWeb"
              name="sitioWeb"
              className={`form-input ${sitioWebError ? 'error' : ''}`}
              value={formData.sitioWeb}
              onChange={handleInputChange}
              onBlur={handleSitioWebBlur}
              placeholder="ejemplo.com"
              required
            />
            {sitioWebError && (
              <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{sitioWebError}</div>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group form-group-industries">
            <label htmlFor="industrias" className="form-label">Industrias</label>
            <MultiSelectTags
              selectedValues={formData.industrias}
              onChange={(values) => setFormData(prev => ({ ...prev, industrias: values }))}
            />
          </div>
        </div>


        <div className="form-group">
          <label htmlFor="mensaje" className="form-label form-label-optional">Mensaje adicional (opcional)</label>
          <textarea
            id="mensaje"
            name="mensaje"
            className="form-textarea"
            value={formData.mensaje}
            onChange={handleInputChange}
            rows={3}
          />
        </div>

        {/* Checkboxes opcionales */}
        <div className="form-group">
          <div className="checkbox-group">
            <label className="checkbox-item no-hover">
              <input
                type="checkbox"
                checked={formData.autorizoWhatsApp}
                onChange={(e) => setFormData(prev => ({ ...prev, autorizoWhatsApp: e.target.checked }))}
              />
              <span className="checkbox-text">Autorizo contacto por WhatsApp para responder mi consulta y coordinar gestiones relacionadas.</span>
            </label>
            <label className="checkbox-item no-hover">
              <input
                type="checkbox"
                checked={formData.deseoNovedades}
                onChange={(e) => setFormData(prev => ({ ...prev, deseoNovedades: e.target.checked }))}
              />
              <span className="checkbox-text">Deseo recibir novedades y promociones por WhatsApp. Puedo darme de baja respondiendo BAJA.</span>
            </label>
          </div>
        </div>

        <button 
          type="submit" 
          className={`btn-primary ${isFormValid() ? 'btn-active' : 'btn-inactive'}`}
          disabled={!isFormValid()}
        >
          {getButtonText()}
        </button>
        
        {/* Mensaje de éxito */}
        {showSuccessMessage && (
          <div className="success-message-inline">
            El formulario fue enviado con éxito, nos pondremos en contacto a la brevedad.
          </div>
        )}
      </form>

      {/* Modal de email duplicado */}
      {duplicateData && (
        <DuplicateEmailModal
          isOpen={showDuplicateModal}
          onClose={handleCloseModal}
          onReplace={handleReplaceQuery}
          onAdd={handleAddToQuery}
          onNewQuery={handleNewQuery}
          previousStands={duplicateData.previousStands}
          newStands={selectedStands}
          previousOrganization={duplicateData.previousOrganization}
          newOrganization={formData.organizacion}
          userEmail={formData.email}
        />
      )}
    </div>
  );
};

export default ReservationForm;
